<template>
  <div class="referral-item">
    <div class="referred-member">
      <div class="referral-image">
        <img
          v-if="conversion.conversionUser.avatar"
          :src="conversion.conversionUser.avatar"
          :alt="conversion.conversionUser.displayName"
        />
        <span v-else class="avatar-placeholder demi">{{
          getFirstLetter(conversion.conversionUser.displayName)
        }}</span>
      </div>
      <div class="referral-details">
        <div class="referral-name">{{ conversion.conversionUser.displayName }}</div>
        <div class="referral-date">
          <div>{{ conversion.conversionableTypeName }}</div>
          <div>{{ conversion.processedAtFormatted }}</div>
        </div>
      </div>
    </div>
    <div v-if="conversion.status === 1" class="referral-reward">
      <div class="reward-amount">
        You Earned
        <span class="reward-value">${{ conversion.rewardAmount }} Wallet Buck</span>
      </div>
      <div class="reward-complete">
        <i aria-hidden="true" class="material-symbols-outlined fill">check_circle</i>
      </div>
    </div>
    <div v-else class="referral-reward">
      <div class="reward-amount">
        <contextual-help :id="`referral_${conversion.id}`" :title="notRewardedTitle" classes="">
          <p>
            {{ notRewardedDesc }}
          </p>
        </contextual-help>
      </div>
      <div v-if="conversion.status === 2" class="not-rewarded">
        <i class="material-symbols-outlined fill">info</i>
      </div>
      <div v-if="conversion.status === 3" class="warning">
        <i class="material-symbols-outlined fill">warning</i>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Referral conversion row
 */
import { computed } from 'vue'
import type { ReferralConversion } from '@/types/referral-types'
import { useUserAuthStore } from '@/stores/user-auth'
import ContextualHelp from '@/components/tool-tips/contextual-help.vue'

/** PROPS **/
const props = defineProps<{
  conversion: ReferralConversion
}>()

/** COMPUTED **/
const notRewardedTitle = computed(() => {
  if (props.conversion.statusReason === 'max_reward_reached') {
    return props.conversion.statusReasonName
  }

  // default for rest
  return 'Unable to Reward'
})

/**
 * Human-readable description for not rewarded.
 */
const notRewardedDesc = computed(() => {
  if (props.conversion.statusReason === 'missing_data') {
    return `Referral account data is missing.`
  }

  if (props.conversion.statusReason === 'max_reward_reached') {
    return `The max reward amount has been reached for the year.`
  }

  if (
    props.conversion.statusReason === 'conversion_ineligible' &&
    props.conversion.conversionableType === 'user_subscription'
  ) {
    return `Referral was not an auto-renew subscription.`
  }

  if (
    props.conversion.statusReason === 'conversion_ineligible' &&
    props.conversion.conversionableType === 'user_purchase'
  ) {
    return `Referral did not purchase a plus pass.`
  }

  if (
    props.conversion.statusReason === 'conversion_ineligible' &&
    props.conversion.conversionableType === 'user_subscription'
  ) {
    return `Referral was not an auto-renew subscription.`
  }

  if (props.conversion.statusReason === 'conversion_disabled') {
    return `Referral transaction was disabled.`
  }

  if (props.conversion.statusReason === 'conversion_canceled') {
    return `Referral subscription canceled before payment.`
  }

  if (props.conversion.statusReason === 'user_deleted') {
    return `Referral account was deleted.`
  }

  if (props.conversion.statusReason === 'conversion_email_unverified') {
    return `Referral email is not verified.`
  }

  if (props.conversion.statusReason === 'conversion_same_user') {
    return `Referral must not be for the same user.`
  }

  if (props.conversion.statusReason === 'conversion_same_email') {
    return `Referral must not be for the same user.`
  }

  if (props.conversion.statusReason === 'referral_code_disabled') {
    return `Referral code is disabled.`
  }

  // just hunman readable code
  return props.conversion.statusReasonName
})

/** METHODS **/
const { getFirstLetter } = useUserAuthStore()
</script>
